import * as React from 'react';
import { useState, useEffect } from 'react';
import { useObjectVal } from 'react-firebase-hooks/database';
import { Row, Col, Button, Collapse, Nav, NavItem, NavLink } from 'reactstrap';
import { AccountsTable } from '../AccountsTable';
import { SelectColumnFilter } from '../../../components/table/SelectColumnfilter';
import { Redirect } from 'react-router-dom';
import { Can } from '../../../auth/Can';
import { ExportToExcel } from '../ExportToExcel';
import { DefaultColumnFilter } from '../../../components/table/DefaultFilter';
import { resetPageIndexOnFilterAction } from '../../../components/table/resetPageIndexOnFilterAction';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import { Loader } from '../../../components/Loader';
import { Enumerations } from '../../enumerations/enumerationsModel';
import { Credential, fuzzyTextFilterFnExp } from '../AccountsContainer';
import { logger } from '../../../logging';
import { useAuthState } from 'react-firebase-hooks/auth';
import moment from 'moment';
import { ErrorIcon, TickIcon } from '../../../components/Icons';
import { ColumnsSelector } from '../../../components/table/ColumnsSelector';
import * as firebaseService from '../../../services/firebase';
import * as R from 'ramda';
import { Countries } from '../../country-manager/countriesModel';
import { getAuth } from 'firebase/auth';
import { ref } from '../../../utils/firebase';
import { useFrozenAccountsTablePrefs } from '../useFrozenAccountsTablePrefs';
import { useAccountsTablePrefsSync } from '../useAccountsTablePrefsSync';
import { AccountsResetFiltersButton } from '../AccountsResetFiltersButton';

const pagesStyles = require('../../pages.css');
const accountsStyles = require('../accounts.css');

import classnames from 'classnames';

const TABS = {
  ACTIVE: 0,
  INACTIVE: 1,
  DECOMM_IN_PROGRESS: 2,
  DECOMM_COMPLETE: 3,
};

export interface BrazeAccount {
  account_name: string;
  account_id: string;
  account_level: string;
  canopus_id: string;
  party_id: number;
  label: string;
  country_code: string;
  language_code: string;
  timezone: string;
  active: boolean;
  created_by: string;
  created_at: number;
  updated_by?: string;
  updated_at?: number;
}

interface Accounts {
  accounts: BrazeAccount[];
}

const getEnumLabels = (
  values,
  enumerations: Enumerations,
  countriesModel: Countries
) => {
  const enumMappings = {
    country_code: 'operatingCountries',
    account_level: 'brazeAccountLevels',
  };

  const enumsToLookup = ['country_code', 'account_level'];

  const labels = { ...values };
  try {
    enumsToLookup.forEach((type) => {
      // logger.info('type: ', type, ' values[type]: ', values[type], 'enumerations.enumerations[enumMappings[type]][values[type]].label:', enumerations.enumerations[enumMappings[type]][values[type]].label);
      if (type === 'country_code') {
        labels[type] = countriesModel.getName(values[type]);
      } else {
        labels[type] = enumerations.getLabel(enumMappings[type], values[type]);
      }
    });
  } catch (e) {
    logger.error(
      e,
      ' account_name: ',
      values.account_name,
      values,
      enumerations.enumerations
    );
  }

  return labels;
};

export const Accounts = ({
  enumerations,
  showActive,
  setShowActive,
  countriesModel,
  onResetAccountsFilters,
}) => {
  const TABLE_NAME = 'brazeAccounts';
  const damApp = firebaseService.getDAMApp();
  const damAuth = getAuth(damApp);
  // get accounts from firebase hooks here
  const accountsRef = ref('accounts/braze');

  const [createAccount, setCreateAccount] = useState(false);
  //const [showInactiveAccounts, setShowInactiveAccounts] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [accountsList, loading, error] = useObjectVal(accountsRef as any);
  const [values, setValues] = useState<any[]>([]);

  const [user, userLoading] = useAuthState(damAuth as any);

  const [isColumnsSelectOpen, setIsColumnsSelectOpen] = useState(false);
  const uid = user ? user.uid : '';
  const userColsRef = ref(`user_preferences/${uid}/columns/${TABLE_NAME}`);
  const [userCols, userColsLoading, userColsError] = useObjectVal(
    userColsRef as any
  );

  const defaultTablePrefs = React.useMemo(
    () => ({
      sortBy: [{ id: 'created_at', desc: true }] as Array<{
        id: string;
        desc: boolean;
      }>,
      pageSize: 20,
      pageIndex: 0,
    }),
    []
  );

  const { frozenPrefs, prefsReady } = useFrozenAccountsTablePrefs(
    uid,
    TABLE_NAME,
    userColsLoading,
    defaultTablePrefs
  );

  // when accountsList changes or enumerations changes
  // and there are no values yet
  // put values into the correct shape + lookup enumeration labels + update state with mapped values
  useEffect(() => {
    if (accountsList && enumerations && countriesModel) {
      const accounts = Object.keys(accountsList).filter((k) => {
        switch (activeTab) {
          case TABS.ACTIVE:
            return accountsList[k].active === true;
          case TABS.INACTIVE:
            return (
              accountsList[k].active === false &&
              !accountsList[k].decommissioning_status?.status
            );
          case TABS.DECOMM_IN_PROGRESS:
            return (
              accountsList[k].decommissioning_status?.status?.toLowerCase() ===
                'pending' ||
              accountsList[k].decommissioning_status?.status?.toLowerCase() ===
                'failed'
            );
          case TABS.DECOMM_COMPLETE:
            return (
              accountsList[k].decommissioning_status?.status?.toLowerCase() ===
              'complete'
            );
        }
      });

      setValues(
        accounts.map((k) => {
          const value = accountsList[k];
          const labels = getEnumLabels(value, enumerations, countriesModel);
          return { ...labels, key: k };
        })
      );
    }
  }, [accountsList, enumerations, activeTab, countriesModel]); // when accountsList or enumerations changes

  const newAccount = () => {
    setCreateAccount(true);
  };

  const updateTableData = (rowIndex, columnId, value) => {
    setValues((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'DAM key',
        accessor: 'key',
        id: 'key',
      },
      {
        Header: 'Braze Workspace Name',
        accessor: 'account_name',
        id: 'account_name',
      },
      {
        Header: 'Party ID',
        accessor: 'party_id',
        id: 'party_id',
      },
      {
        Header: 'Party Name',
        accessor: 'party_name',
        id: 'party_name',
      },
      {
        Header: 'Data Acquisition Processing Method',
        accessor: 'data_acquisition_processing_method',
        Filter: SelectColumnFilter,
        filter: 'equals',
        id: 'data_acquisition_processing_method',
      },
      {
        Header: 'Data Acquisition Selection',
        accessor: 'data_acquisition_selection',
        Filter: SelectColumnFilter,
        filter: 'equals',
        id: 'data_acquisition_selection',
      },
      {
        Header: 'B2C/B2B',
        accessor: 'is_b2c_or_b2b',
        Filter: SelectColumnFilter,
        id: 'is_b2c_or_b2b',
      },
      {
        Header: 'Braze App Group ID',
        accessor: 'account_id',
        id: 'account_id',
      },
      {
        Header: 'Created',
        accessor: (row) =>
          moment.unix(+row.created_at).format('YYYY-MM-DD HH:mm:ss'),
        id: 'created_at',
      },

      {
        Header: 'Modified',
        accessor: (row) =>
          moment
            .unix(+row.modified_at || +row.created_at)
            .format('YYYY-MM-DD HH:mm:ss'),
        id: 'modified_at',
      },
    ],
    []
  );

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFnExp,
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  // memoize values - the heavy lifting is now done in the above useEffect
  const records = React.useMemo(() => values, [values]);

  let hiddenColumns: any = R.keys(
    R.pickBy((val, key) => val === false, userCols)
  );

  const tableData = useTable(
    {
      columns,
      data: records,
      filterTypes,
      defaultColumn,
      stateReducer: resetPageIndexOnFilterAction,
      autoResetFilters: false,
      autoResetPage: false,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          {
            id: 'created_at',
            desc: true,
          },
        ],
        pageSize: 20,
        pageIndex: 0,
      },
      autoResetHiddenColumns: false,
      updateTableData,
    } as any,
    useFilters,
    useSortBy,
    usePagination
  ) as any;

  // Hydrate from RTDB then debounce-persist table + tab state.
  useAccountsTablePrefsSync({
    uid,
    tableName: TABLE_NAME,
    prefsReady,
    frozenPrefs,
    tableData,
    activeTab,
    setActiveTab,
  });

  React.useEffect(() => {
    hiddenColumns = R.keys(R.pickBy((val, key) => val === false, userCols));
    tableData.setHiddenColumns(hiddenColumns);
  }, [userCols]);

  if (createAccount === true) {
    return (
      <React.Fragment>
        <Redirect to="/accounts/new/braze" />
      </React.Fragment>
    );
  }

  if (loading || userColsLoading || !prefsReady) {
    return <Loader loading={true} />;
  }

  if (error) {
    return <div>{`Error: ${error.message}`}</div>;
  }

  if (userColsError) {
    return <div>Error loading cols</div>;
  }

  // depends on braze account name being unique
  const accountsFiltered = () => {
    const rows = tableData.rows.map((r) => r.values);

    return records.filter((v: any) => {
      const found = rows.find((r) => {
        return r.account_name === v.account_name;
      });

      if (found) {
        return true;
      }

      return false;
    });
  };

  const toggleColumnsSelect = () =>
    setIsColumnsSelectOpen(!isColumnsSelectOpen);

  return (
    <div className={pagesStyles.fullHeight}>
      {values && (
        <div className={`${pagesStyles.noPadding} ${pagesStyles.fullHeight}`}>
          <Row>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === TABS.ACTIVE })}
                  onClick={() => setActiveTab(TABS.ACTIVE)}
                >
                  Active Accounts
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === TABS.INACTIVE,
                  })}
                  onClick={() => setActiveTab(TABS.INACTIVE)}
                >
                  Inactive Accounts
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === TABS.DECOMM_IN_PROGRESS,
                  })}
                  onClick={() => setActiveTab(TABS.DECOMM_IN_PROGRESS)}
                >
                  Decommission In Progress
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === TABS.DECOMM_COMPLETE,
                  })}
                  onClick={() => setActiveTab(TABS.DECOMM_COMPLETE)}
                >
                  Decommissioned
                </NavLink>
              </NavItem>
            </Nav>
          </Row>
          <Row className={pagesStyles.headerRow}>
            <Col sm={{ size: 12 }} className={pagesStyles.noPadding}>
              <Can I="create" an="account">
                <Button
                  onClick={(e) => newAccount()}
                  className={accountsStyles.button}
                >
                  Create New Account
                </Button>
              </Can>
              <Can I="export" an="accounts">
                <ExportToExcel
                  csvData={accountsFiltered()}
                  fileName={'accounts-braze'}
                  user={user}
                />
              </Can>
              <Button
                onClick={toggleColumnsSelect}
                className={accountsStyles.button}
              >
                Columns
              </Button>
              <AccountsResetFiltersButton
                onResetAccountsFilters={onResetAccountsFilters}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Collapse isOpen={isColumnsSelectOpen}>
                <ColumnsSelector
                  columns={columns}
                  userCols={userCols}
                  tableName={TABLE_NAME}
                />
              </Collapse>
            </Col>
          </Row>
          <div className={pagesStyles.fullHeight}>
            <Row className={pagesStyles.fullHeight}>
              <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
                <AccountsTable {...tableData} system="braze" />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};
